import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { E2IConfig, Enquiry, Job, JobFieldsInput, JobKind, JobLineInput, PartyRef, Quotation, UserOption } from '../../types/e2i';
import { cleanDateInput, dateOrNull, errorText, formatAmount, formatQuantity, quotationLabel, round2 } from '../../utils/e2i';
import JobLineDialog from './JobLineDialog';
import JobProjectPicker from './JobProjectPicker';
import UserPicker from './UserPicker';

export type JobDialogInput = JobFieldsInput & { calReason?: string; lines?: JobLineInput[] };

interface Props {
  open: boolean;
  kind: JobKind;
  /** The job being edited; null for a new one. */
  job: Job | null;
  /** A new job from a quotation: the client-approved quotation (its priced lines become the job's). */
  quotation: Quotation | null;
  enquiry: Enquiry;
  config: E2IConfig;
  companyId: number;
  currency: string;
  onClose: () => void;
  onSave: (input: JobDialogInput) => Promise<void>;
}

const toLineInputs = (job: Job | null): JobLineInput[] =>
  (job?.lines || []).map((l, i) => ({
    lineNum: i + 1,
    description: l.description,
    quantity: l.quantity,
    uom: l.uom,
    unitPrice: l.unitPrice,
    amount: l.amount,
    salesItem: l.salesItem,
    account: l.account,
  }));

/** The job: what is done, for which project, under which contract dates and terms. */
const JobDialog: React.FC<Props> = ({ open, kind, job, quotation, enquiry, config, companyId, currency, onClose, onSave }) => {
  const [description, setDescription] = useState('');
  const [jobType, setJobType] = useState('');
  const [project, setProject] = useState<PartyRef | null>(null);
  const [location, setLocation] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [engineer, setEngineer] = useState<UserOption | null>(null);
  const [paymentTerms, setPaymentTerms] = useState<number | ''>('');
  const [contractStart, setContractStart] = useState('');
  const [contractEnd, setContractEnd] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [graceDays, setGraceDays] = useState('');
  const [remarks, setRemarks] = useState('');
  const [calReason, setCalReason] = useState('');
  const [lines, setLines] = useState<JobLineInput[]>([]);
  const [lineDialog, setLineDialog] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const firstScopeLine = (quotation?.scope || '').split('\n').map((s) => s.trim()).find(Boolean) || '';
    setDescription(job?.description || firstScopeLine.slice(0, 254) || enquiry.subject || '');
    setJobType(job?.jobType || (config.jobTypes.length === 1 ? config.jobTypes[0].code : ''));
    setProject(job?.project || enquiry.project || null);
    setLocation(job?.location || '');
    setCostCenter(job?.costCenter || '');
    setEngineer(job?.engineer ? { userId: job.engineer.userId, name: job.engineer.name } : null);
    setPaymentTerms(job?.paymentTerms ?? '');
    setContractStart(job?.contractStart ? job.contractStart.slice(0, 10) : '');
    setContractEnd(job?.contractEnd ? job.contractEnd.slice(0, 10) : '');
    setAutoRenew(!!job?.autoRenew);
    setGraceDays(job?.gracePeriodDays !== null && job?.gracePeriodDays !== undefined ? String(job.gracePeriodDays) : '');
    setRemarks(job?.remarks || '');
    setCalReason(job?.calReason || '');
    setLines(toLineInputs(job));
    setBusy(false);
    setTried(false);
    setError(null);
    // Only when the dialog opens or shows another job: typing must not be reset by the page re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job]);

  const isCal = kind === 'CAL';
  const fixedLines: Array<{ amount: number }> = !isCal ? (job ? job.lines : quotation?.lines || []) : [];
  const fixedTotal = round2(fixedLines.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const calTotal = round2(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const start = dateOrNull(contractStart);
  const end = dateOrNull(contractEnd);
  const grace = graceDays.trim() ? Number(graceDays) : null;

  const problems: string[] = [];
  if (!description.trim()) problems.push('Describe the job');
  if (!project?.code) problems.push('Choose the project');
  if (config.jobTypes.length && !jobType) problems.push('Choose the job type');
  if (contractStart.trim() && !start) problems.push('The contract start is not a date');
  if (contractEnd.trim() && !end) problems.push('The contract end is not a date');
  if (start && end && end < start) problems.push('The contract ends before it starts');
  if (grace !== null && (!Number.isInteger(grace) || grace < 0 || grace > 3650)) problems.push('The grace period is a number of days (0 to 3650)');
  if (isCal && !calReason.trim()) problems.push('Give the reason for a job without a quotation');
  if (isCal && !lines.length) problems.push('Add at least one line');

  const save = async () => {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const fields: JobFieldsInput = {
        description: description.trim(),
        jobType: jobType || null,
        location: location.trim() || null,
        costCenter: costCenter.trim() || null,
        engineerUserId: engineer?.userId ?? null,
        paymentTerms: paymentTerms === '' ? null : Number(paymentTerms),
        contractStart: start,
        contractEnd: end,
        autoRenew,
        gracePeriodDays: grace,
        remarks: remarks.trim() || null,
        projectCode: project?.code || null,
      };
      await onSave(isCal ? { ...fields, calReason: calReason.trim(), lines: lines.map((l, i) => ({ ...l, lineNum: i + 1 })) } : fields);
    } catch (e: any) {
      setError(errorText(e, 'The job could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  const applyLine = (line: Omit<JobLineInput, 'lineNum'>) => {
    setLines((ls) => {
      const list = [...ls];
      if (lineDialog.index === null) list.push({ ...line, lineNum: list.length + 1 });
      else list[lineDialog.index] = { ...list[lineDialog.index], ...line };
      return list;
    });
    setLineDialog((s) => ({ ...s, open: false }));
  };

  const title = job
    ? `Job ${job.jobNo || ''}`.trim()
    : isCal ? 'New job without a quotation' : `New job from ${quotationLabel(quotation?.quotationNo, quotation?.revision)}`;

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="e2i-job-title">
      <DialogTitle id="e2i-job-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {isCal ? (
            <Grid item xs={12}>
              <TextField
                id="e2i-job-reason"
                label="Why there is no quotation"
                value={calReason}
                onChange={(e) => setCalReason(e.target.value.slice(0, 2000))}
                multiline
                minRows={2}
                fullWidth
                required
                helperText="Exceptional: e.g. an emergency call-out under an existing contract"
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField id="e2i-job-desc" label="Job description" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 254))} fullWidth required />
          </Grid>
          <Grid item xs={12} md={6}>
            <JobProjectPicker id="e2i-job-project" companyId={companyId} mainProject={enquiry.project} value={project} onChange={setProject} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="e2i-job-type"
              select
              label="Job type"
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
              fullWidth
              required={config.jobTypes.length > 0}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              helperText={config.jobTypes.length ? undefined : 'No job types in Setup'}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {jobType && !config.jobTypes.some((t) => t.code === jobType) ? <MenuItem value={jobType}>{jobType}</MenuItem> : null}
              {config.jobTypes.map((t) => <MenuItem key={t.code} value={t.code}>{t.name} ({t.code})</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="e2i-job-terms"
              select
              label="Payment terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value === '' ? '' : Number(e.target.value))}
              fullWidth
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value=""><em>The customer&apos;s (SAP)</em></MenuItem>
              {paymentTerms !== '' && !config.paymentTerms.some((p) => Number(p.code) === Number(paymentTerms)) ? <MenuItem value={paymentTerms}>{paymentTerms}</MenuItem> : null}
              {config.paymentTerms.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField id="e2i-job-location" label="Location" value={location} onChange={(e) => setLocation(e.target.value.slice(0, 100))} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField id="e2i-job-cc" label="Cost center" value={costCenter} onChange={(e) => setCostCenter(e.target.value.slice(0, 50))} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <UserPicker id="e2i-job-engineer" companyId={companyId} label="Engineer" value={engineer} onChange={setEngineer} helperText="Sees the enquiry and is told about its invoice requests" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="e2i-job-start" type="date" label="Contract start" value={contractStart} onChange={(e) => setContractStart(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              id="e2i-job-end"
              type="date"
              label="Contract end"
              value={contractEnd}
              onChange={(e) => setContractEnd(cleanDateInput(e.target.value))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: start || undefined }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="e2i-job-grace" label="Grace period (days)" value={graceDays} onChange={(e) => setGraceDays(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} inputProps={{ inputMode: 'numeric' }} fullWidth />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel control={<Switch checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />} label="Renews automatically" />
          </Grid>
          <Grid item xs={12}>
            <TextField id="e2i-job-remarks" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value.slice(0, 4000))} multiline minRows={2} fullWidth />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" component="h3" sx={{ mr: 'auto' }}>Lines</Typography>
              {isCal ? <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setLineDialog({ open: true, index: null })}>Add line</Button> : null}
            </Stack>
            {isCal ? (
              lines.length ? (
                <Stack component="ol" spacing={0.75} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {lines.map((l, i) => (
                    <Stack component="li" key={i} direction="row" spacing={1} alignItems="center" sx={{ borderBottom: '1px solid #eceff1', pb: 0.75 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">{i + 1}. {l.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatQuantity(l.quantity)}{l.uom ? ` ${l.uom}` : ''} × {formatAmount(l.unitPrice)}{(config.docMode === 'Service' ? l.account : l.salesItem) ? ` · ${config.docMode === 'Service' ? l.account : l.salesItem}` : ''}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{formatAmount(l.amount)}</Typography>
                      <IconButton size="small" aria-label={`Edit line ${i + 1}`} onClick={() => setLineDialog({ open: true, index: i })}><EditOutlinedIcon fontSize="small" /></IconButton>
                      <IconButton size="small" aria-label={`Remove line ${i + 1}`} onClick={() => setLines((ls) => ls.filter((_x, j) => j !== i).map((x, j) => ({ ...x, lineNum: j + 1 })))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>Total <strong>{formatAmount(calTotal)} {currency}</strong> before VAT</Typography>
                </Stack>
              ) : <Typography variant="body2" color="text.secondary">No lines yet: one line per period or milestone; each is invoiced once.</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                The quotation&apos;s {fixedLines.length} priced line{fixedLines.length === 1 ? '' : 's'} ({formatAmount(fixedTotal)} {currency} before VAT) become the job&apos;s lines; they cannot be changed here.
              </Typography>
            )}
          </Grid>
        </Grid>
        {tried && problems.length ? <Alert severity="warning" sx={{ mt: 2 }}>{problems.join('; ')}.</Alert> : null}
        {error ? <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy}>{busy ? 'Saving…' : job ? 'Save job' : 'Create job'}</Button>
      </DialogActions>

      <JobLineDialog
        open={lineDialog.open}
        line={lineDialog.index !== null ? lines[lineDialog.index] || null : null}
        companyId={companyId}
        docMode={config.docMode}
        currency={currency}
        onClose={() => setLineDialog((s) => ({ ...s, open: false }))}
        onSave={applyLine}
      />
    </ResponsiveDialog>
  );
};

export default JobDialog;
